import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../styles/popup-styles.css';

import {
  Map,
  MapRef,
  NavigationControl,
  Popup,
  ScaleControl,
  ViewStateChangeEvent,
} from 'react-map-gl';

import { Flex, Progress } from '@chakra-ui/react';
import { PublicListingFragment } from '@client/graphql/__generated__/types';
import debounce from 'lodash/debounce';
import { MapEvent } from 'mapbox-gl';
import { RefObject, useCallback, useMemo } from 'react';
import {
  getIsUserListing,
  getListingPrice,
} from '~/apps/consumer/utils/listing.utils';
import { bboxToLngLatBounds } from '~/apps/consumer/utils/map.utils';
import { useProdFeatureFlagEnabled } from '~/common/hooks/useProdFeatureFlagEnabled';
import { useAppContext } from '~/services/main/contexts/AppContext';
import { PropertyDetailsCard } from '../../Listings/ListingCard/PropertyDetailsCard';
import { PublicListingCard } from '../../Listings/ListingCard/PublicListingCard';
import { AddOffMarketListingPrompt } from '../AddOffMarketListingPrompt';
import { DEFAULT_POSITION, MLS_TO_BBOX_MAP } from '../constants';
import { useExploreContext } from '../ExploreContext';
import { convertBoundsToBbox } from '../helpers';
import { ExploreMapMarker } from './ExploreMapMarker';

interface IExploreMapProps {
  mapRef: RefObject<MapRef>;
  visibility?: 'visible' | 'hidden';
}

export const ExploreMap = ({
  mapRef,
  visibility = 'visible',
}: IExploreMapProps) => {
  const isOffMarketListingsEnabled = useProdFeatureFlagEnabled(
    'off-market-listings'
  );
  const {
    selectedListing,
    setSelectedListing,
    listings,
    updateFilters,
    loading,
    utmParams,
    foundPropertyDetails,
    setFoundPropertyDetails,
    shouldPromptForOffMarketListing,
    setShouldPromptForOffMarketListing,
    searchResult,
  } = useExploreContext();
  const { user } = useAppContext();

  const mlsOSN = user?.licenses?.[0]?.mls?.originatingSystemName;

  const markers = useMemo(() => {
    return listings.map(({ mlsListing, listing }) => {
      if (!mlsListing.geo?.location?.lat || !mlsListing.geo?.location?.lon) {
        return null;
      }

      const isUserListing = getIsUserListing(
        listing as PublicListingFragment | undefined,
        user
      );

      const price = getListingPrice(mlsListing);

      return (
        <ExploreMapMarker
          key={`marker-${mlsListing.id}`}
          isPremium={isUserListing}
          lat={mlsListing.geo.location.lat}
          lng={mlsListing.geo.location.lon}
          price={price}
          zoom={mapRef.current?.getZoom() || DEFAULT_POSITION.zoom}
          onClick={(event) => {
            event.originalEvent.stopPropagation();
            setSelectedListing({
              listing,
              mlsListing,
            });
          }}
        />
      );
    });
  }, [listings, setSelectedListing, mapRef, user]);

  const fetchListings = useCallback(
    (event: MapEvent) => {
      const bounds = event.target.getBounds();
      const bbox = convertBoundsToBbox(bounds);

      if (bbox) {
        updateFilters({
          bbox,
        });
      }
    },
    [updateFilters]
  );

  const handleMove = useMemo(
    () =>
      debounce((event: ViewStateChangeEvent) => {
        fetchListings(event);
      }, 300),
    [fetchListings]
  );

  return (
    <Flex
      direction={{ base: 'column-reverse', xl: 'column' }}
      height={{
        base: visibility === 'visible' ? '100%' : '0',
        xl: '100%',
      }}
      visibility={visibility}
      width="100%"
    >
      <Progress
        isIndeterminate
        height="2px"
        size="xs"
        visibility={loading ? 'visible' : 'hidden'}
        width="100%"
      />
      <Map
        ref={mapRef}
        initialViewState={{
          bounds: bboxToLngLatBounds(
            mlsOSN && MLS_TO_BBOX_MAP[mlsOSN]
              ? MLS_TO_BBOX_MAP[mlsOSN]
              : DEFAULT_POSITION.bbox
          ),
        }}
        mapStyle="mapbox://styles/mapbox/navigation-guidance-night-v4"
        mapboxAccessToken={window.ENVS.mapboxApiKey}
        minZoom={3}
        testMode={window.ENVS.environment === 'e2e'}
        onLoad={fetchListings}
        onMove={handleMove}
      >
        <NavigationControl position="bottom-right" />
        <ScaleControl />
        {markers}
        {selectedListing?.mlsListing && (
          <Popup
            latitude={selectedListing.mlsListing.geo.location.lat}
            longitude={selectedListing.mlsListing.geo.location.lon}
            offset={15}
            style={{
              maxWidth: '260px',
              minWidth: '260px',
              zIndex: 2,
              borderRadius: '12px',
            }}
            onClose={() => setSelectedListing(null)}
          >
            <PublicListingCard
              listing={selectedListing.listing}
              mlsListing={selectedListing.mlsListing}
              utmParams={utmParams}
            />
          </Popup>
        )}
        {isOffMarketListingsEnabled &&
          shouldPromptForOffMarketListing &&
          searchResult && (
            <Popup
              latitude={searchResult.geometry.coordinates[1]}
              longitude={searchResult.geometry.coordinates[0]}
              offset={15}
              style={{
                maxWidth: '260px',
                minWidth: '260px',
                zIndex: 2,
                borderRadius: '12px',
              }}
              onClose={() => setShouldPromptForOffMarketListing(false)}
            >
              <AddOffMarketListingPrompt initialGeocode={searchResult} />
            </Popup>
          )}
        {isOffMarketListingsEnabled && foundPropertyDetails && (
          <Popup
            latitude={foundPropertyDetails.geo.lat}
            longitude={foundPropertyDetails.geo.lng}
            offset={15}
            style={{
              maxWidth: '260px',
              minWidth: '260px',
              zIndex: 2,
              borderRadius: '12px',
            }}
            onClose={() => setFoundPropertyDetails(null)}
          >
            <PropertyDetailsCard
              showButton
              propertyDetails={foundPropertyDetails}
            />
          </Popup>
        )}
      </Map>
    </Flex>
  );
};
